import { queryOptions } from "@tanstack/react-query";
import type { WorkspaceTeammatesService } from "@/services/workspace-teammates.service";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The Digital Teammates settings screens (NG-2622, WS2-F):
 *
 * - **admin screen**, `GET {ws}/teammates/`, `[slug, uid, 'teammates', 'admin']`:
 *   every teammate available here, its workspace switch and whether it waits on
 *   the admin's consent to a newer version. Read by Workspace settings → Digital
 *   Teammates (`workspace.edit`).
 * - **member screen**, `GET {ws}/teammates/mine/`, `[slug, uid, 'teammates', 'mine']`:
 *   the teammates enabled here that the caller may use, and where their own
 *   instance stands. Read by Profile → Digital Teammates (any member).
 *
 * Both **access-sensitive**: the switch and the consent decide whether a
 * teammate may act at all, so they are re-read on mount, focus and reconnect
 * rather than held for minutes. Keyed by workspace, so a workspace switch never
 * shows, or writes over, another workspace's teammates. Plain GETs, so
 * `"safe-read"`.
 *
 * @writers `useAdminTeammates`' `setEnabled` (the switch, and Approve on a
 * teammate that needs re-consent) writes the change into the admin screen at
 * once, cancelling any read in flight first, puts the previous screen back if
 * the server refuses, and settles the server's row into it when it answers.
 * Writes run one at a time per workspace, in the order they were made.
 * `useMyTeammates`' `turnOff` writes the member screen the same way; its
 * `turnOn` (the consent) waits for the server and settles the server's row, and
 * re-reads the screen when the server refuses it because the teammate changed
 * or waits on an admin.
 */

/** The admin screen's key. */
export function adminTeammatesKey(scope: QueryScope) {
  return scopedKey(scope, "teammates", "admin");
}

/** Every teammate available in the workspace, with its switch. */
export function adminTeammatesQuery(
  service: Pick<WorkspaceTeammatesService, "getAdminScreen">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: adminTeammatesKey(scope),
    queryFn: scopedRead((request) => service.getAdminScreen(scope.tenant, request), {
      retry: "safe-read",
    }),
  });
}

/** The member screen's key. */
export function myTeammatesKey(scope: QueryScope) {
  return scopedKey(scope, "teammates", "mine");
}

/** The teammates the caller may use here, and where their own stands. */
export function myTeammatesQuery(
  service: Pick<WorkspaceTeammatesService, "getMemberScreen">,
  scope: QueryScope,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: myTeammatesKey(scope),
    queryFn: scopedRead((request) => service.getMemberScreen(scope.tenant, request), {
      retry: "safe-read",
    }),
  });
}
